import { Col, Row, Card, message, Spin, Badge, Calendar } from "antd";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import MotionHoc from "../../../utils/MotionHoc";
import axios from "axios";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { HEADERS_EXPORT_BEN, POST_MAIN_LAWYER } from "../../API/apiUrls";
import AppointmentData from "./modal/AppointmentData";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const Main = () => {
  // ============== STATES ==============
  const ROLE_ID = localStorage.getItem("ROLE_ID");
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const companyId = localStorage.getItem("COMPANY_ID");

  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [dataModal, setDataModal] = useState([]);
  const [dateSelect, setDateSelect] = useState("");
  const [panelMode, setPanelMode] = useState("month");
  const [currentDate, setCurrentDate] = useState(dayjs());

  // ============== CONSTANTS ==============
  const STATUS = {
    UPCOMING: 5, // นัดที่กำลังจะมาถึง - สีแดง
    COMPLETED: 8, // นัดที่ผ่านมาแล้ว - สีเขียว
  };

  const STATUS_COLORS = {
    [STATUS.UPCOMING]: "#ff4d4f", // สีแดง
    [STATUS.COMPLETED]: "#52c41a", // สีเขียว
    default: "#1890ff", // สีน้ำเงิน (default)
  };

  // ============== LOAD DATA ==============
  useEffect(() => {
    loadData();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);

    const payload = {
      userId: userId,
    };

    try {
      const response = await axios.post(POST_MAIN_LAWYER, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      if (response.data && Array.isArray(response.data)) {
        const formattedData = response.data.map((item, index) => ({
          ...item,
          key: index + 1,
        }));

        filterDataLawyer(formattedData);
        console.log("Loaded data:", formattedData);
      } else {
        setDataArr([]);
        message.warning("ไม่พบข้อมูลนัดหมาย");
      }
    } catch (error) {
      console.error("Error loading data:", error);
      message.error(`เกิดข้อผิดพลาด: ${error.message}`);
      setDataArr([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // ============== FILTER DATA ==============
  const filterDataLawyer = useCallback(
    (data) => {
      if (!Array.isArray(data)) {
        console.error("data is not an array");
        setDataArr([]);
        return;
      }

      const filtered = data.filter((item) => {
        if (!item.considerationDate || !item.blackNumber) {
          return false;
        }
        console.log("item", item);

        const hasPermission =
          item.userId === userId || ROLE_ID === "1" || ROLE_ID === "2";

        return hasPermission;
      });
      console.log("filtered", filtered);

      const sorted = filtered.sort(
        (a, b) =>
          dayjs(a.considerationDate).valueOf() -
          dayjs(b.considerationDate).valueOf(),
      );

      setDataArr(sorted);
      console.log("Filtered data:", sorted.length, "items");
    },
    [userId, ROLE_ID],
  );

  // ============== CALENDAR FUNCTIONS ==============

  // ✅ ดึงข้อมูลตามวัน
  const getListData = useCallback(
    (value) => {
      if (!dataArr || dataArr.length === 0) return [];

      return dataArr.filter((item) =>
        dayjs(item.considerationDate).isSame(value, "day"),
      );
    },
    [dataArr],
  );

  // ✅ นับจำนวนตามเดือน (แยกตาม status)
  const getMonthData = useCallback(
    (value) => {
      if (!dataArr || dataArr.length === 0)
        return { total: 0, upcoming: 0, completed: 0 };

      const targetMonth = value.month();
      const targetYear = value.year();

      const monthData = dataArr.filter((item) => {
        const date = dayjs(item.considerationDate);
        return date.year() === targetYear && date.month() === targetMonth;
      });

      return {
        total: monthData.length,
        upcoming: monthData.filter(
          (item) =>
            item.currentStatus === STATUS.UPCOMING &&
            item.considerationDate && // เช็ค null ก่อน
            dayjs(item.considerationDate).isAfter(dayjs(), "day"),
        ).length,
        completed: monthData.filter(
          (item) =>
            item.considerationDate && // เช็ค null ก่อน
            dayjs(item.considerationDate).isBefore(dayjs(), "day"),
          // item.currentStatus === STATUS.COMPLETED
        ).length,
      };
    },
    [dataArr],
  );

  // ✅ ฟังก์ชันเลือกสีตาม status
  const getStatusColor = useCallback((currentStatus) => {
    return STATUS_COLORS[currentStatus] || STATUS_COLORS.default;
  }, []);

  // ✅ ฟังก์ชันดึงข้อความสถานะ
  const getStatusText = useCallback((currentStatus) => {
    if (currentStatus === STATUS.UPCOMING) return "กำลังจะมาถึง";
    if (currentStatus === STATUS.COMPLETED) return "ผ่านมาแล้ว";
    return "อื่นๆ";
  }, []);

  // ✅ Render เดือน
  const monthCellRender = useCallback(
    (value) => {
      const { total, upcoming, completed } = getMonthData(value);

      if (total === 0) return null;

      return (
        <div style={{ textAlign: "center", paddingTop: 8 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            {upcoming > 0 && (
              <Badge
                count={upcoming}
                style={{ backgroundColor: STATUS_COLORS[STATUS.UPCOMING] }}
                overflowCount={99}
              />
            )}{" "}
            {completed > 0 && (
              <Badge
                count={completed}
                style={{ backgroundColor: STATUS_COLORS[STATUS.COMPLETED] }}
                overflowCount={99}
              />
            )}
          </div>
        </div>
      );
    },
    [getMonthData],
  );

  // ✅ Render วัน
  const dateCellRender = useCallback(
    (value) => {
      const listData = getListData(value);
      const count = listData.length;

      if (count === 0) return null;

      // แยกข้อมูลตาม status
      const upcomingItems = listData.filter(
        (item) =>
          item.currentStatus === STATUS.UPCOMING &&
          item.considerationDate && // เช็ค null ก่อน
          dayjs(item.considerationDate).isAfter(dayjs(), "day"),
      );
      const completedItems = listData.filter(
        (item) =>
          item.considerationDate && // เช็ค null ก่อน
          dayjs(item.considerationDate).isBefore(dayjs(), "day"),
        // item.currentStatus === STATUS.COMPLETED
      );

      return (
        <div style={{ fontSize: "12px" }}>
          {/* แสดง Badge จำนวน */}
          <div
            style={{
              textAlign: "center",
              marginBottom: 4,
              display: "flex",
              justifyContent: "center",
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            {upcomingItems.length > 0 && (
              <Badge
                count={upcomingItems.length}
                style={{ backgroundColor: STATUS_COLORS[STATUS.UPCOMING] }}
                overflowCount={99}
                title="นัดที่กำลังจะมาถึง"
              />
            )}
            {completedItems.length > 0 && (
              <Badge
                count={completedItems.length}
                style={{ backgroundColor: STATUS_COLORS[STATUS.COMPLETED] }}
                overflowCount={99}
                title="นัดที่ผ่านมาแล้ว"
              />
            )}
          </div>

          {/* แสดงรายการ (สูงสุด 2 รายการ) */}
          <ul
            className="events"
            style={{ padding: 0, margin: 0, listStyle: "none" }}
          >
            {listData.slice(0, 2).map((item, index) => {
              const statusColor = getStatusColor(item.currentStatus);

              return (
                <li key={index} style={{ marginBottom: 2 }}>
                  <Badge
                    color={statusColor}
                    text={
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "11px",
                        }}
                      >
                        <div style={{ fontWeight: "bold" }}>
                          {item.blackNumber || "ไม่ระบุเลขคดี"}
                        </div>
                        <div style={{ color: "#666" }}>
                          {dayjs(item.considerationDate).format("HH:mm")} น.
                        </div>
                      </div>
                    }
                  />
                </li>
              );
            })}
            {count > 2 && (
              <li
                style={{
                  textAlign: "center",
                  color: "#1890ff",
                  fontSize: "11px",
                }}
              >
                +{count - 2} รายการ
              </li>
            )}
          </ul>
        </div>
      );
    },
    [getListData, getStatusColor],
  );

  const cellRender = useCallback(
    (current, info) => {
      if (info.type === "date") return dateCellRender(current);
      if (info.type === "month") return monthCellRender(current);
      return info.originNode;
    },
    [dateCellRender, monthCellRender],
  );

  // ============== EVENT HANDLERS ==============

  const handlePanelChange = useCallback((value, mode) => {
    console.log("Panel changed to:", mode, "Date:", value.format("YYYY-MM"));
    setPanelMode(mode);
    setCurrentDate(value);
  }, []);

  const handleDateSelect = useCallback(
    (date) => {
      console.log("Date selected:", date.format("YYYY-MM-DD"));
      console.log("Current panel mode:", panelMode);

      if (panelMode === "year") {
        console.log(
          "Switching from year to month mode for:",
          date.format("YYYY-MM"),
        );
        setPanelMode("month");
        setCurrentDate(date);
        return;
      }

      if (panelMode === "month") {
        const selectedList = dataArr.filter((item) =>
          dayjs(item.considerationDate).isSame(date, "day"),
        );

        console.log("Selected appointments:", selectedList.length);

        if (selectedList.length > 0) {
          setDateSelect(date.format("YYYY-MM-DD"));
          setDataModal(selectedList);
          setIsModal(true);
        } else {
          message.info("ไม่มีนัดหมายในวันที่เลือก");
        }
      }
    },
    [panelMode, dataArr],
  );

  const handleCloseModal = useCallback(() => {
    setIsModal(false);
    setDataModal([]);
    setDateSelect("");
  }, []);

  // ============== MEMO VALUES ==============

  // ✅ นัดทั้งหมด (สีน้ำเงิน) - status 5 หรือ 8
  const totalAppointments = useMemo(() => {
    return dataArr.filter(
      (item) =>
        // item.currentStatus === STATUS.UPCOMING ||
        // item.currentStatus === STATUS.COMPLETED
        item.considerationDate,
    ).length;
  }, [dataArr]);

  // ✅ นัดที่กำลังจะมาถึง (สีแดง) - status 5
  const upcomingAppointments = useMemo(() => {
    return dataArr.filter(
      (item) =>
        item.considerationDate && // เช็ค null ก่อน
        dayjs(item.considerationDate).isAfter(dayjs(), "day"),
    ).length;
  }, [dataArr]);

  // ✅ นัดที่ผ่านมาแล้ว (สีเขียว) - status 8
  const completedAppointments = useMemo(() => {
    return dataArr.filter(
      (item) =>
        // item.currentStatus === STATUS.COMPLETED ||
        item.considerationDate && // เช็ค null ก่อน
        dayjs(item.considerationDate).isBefore(dayjs(), "day"),
    ).length;
  }, [dataArr]);

  console.log(
    "sss",
    dataArr.filter(
      (item) =>
        item.considerationDate && // เช็ค null ก่อน
        dayjs(item.considerationDate).isBefore(dayjs(), "day"),
    ),
  );

  // ============== RENDER ==============
  return (
    <>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <span style={{ fontSize: "18px", fontWeight: "bold" }}>
              ปฏิทินนัดหมายคดี
            </span>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "normal",
                display: "flex",
                alignItems: "center",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              {/* ✅ นัดทั้งหมด - สีน้ำเงิน */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge
                  count={totalAppointments}
                  style={{ backgroundColor: STATUS_COLORS.default }}
                  overflowCount={999}
                />
                <span style={{ color: "#666" }}>นัดทั้งหมด</span>
              </div>

              {/* ✅ นัดที่กำลังจะมาถึง - สีแดง */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge
                  count={upcomingAppointments}
                  style={{ backgroundColor: STATUS_COLORS[STATUS.UPCOMING] }}
                  overflowCount={999}
                />
                <span style={{ color: "#666" }}>กำลังจะมาถึง</span>
              </div>

              {/* ✅ นัดที่ผ่านมาแล้ว - สีเขียว */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge
                  count={completedAppointments}
                  style={{ backgroundColor: STATUS_COLORS[STATUS.COMPLETED] }}
                  overflowCount={999}
                />
                <span style={{ color: "#666" }}>ผ่านมาแล้ว</span>
              </div>
            </div>
          </div>
        }
      >
        <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
          <Row>
            <Col span={24}>
              {/* ✅ คำแนะนำการใช้งาน */}
              <Card
                size="small"
                style={{
                  marginBottom: 16,
                  background: `rgba(0, 57, 96, 0.08)`,
                  border: `1px solid rgba(0, 57, 96, 0.15)`,
                }}
              >
                <div style={{ fontSize: "13px", color: "#003960" }}>
                  💡 <strong>วิธีใช้:</strong>
                  {panelMode === "year" ? (
                    <span style={{ marginLeft: 8 }}>
                      คลิกที่<strong>เดือน</strong>เพื่อดูปฏิทินของเดือนนั้น
                    </span>
                  ) : (
                    <span style={{ marginLeft: 8 }}>
                      คลิกที่<strong>วันที่</strong>
                      เพื่อดูรายละเอียดนัดหมายในวันนั้น
                    </span>
                  )}
                  <span style={{ marginLeft: 16 }}>
                    |
                    <span
                      style={{
                        marginLeft: 8,
                        color: STATUS_COLORS[STATUS.UPCOMING],
                        fontWeight: "bold",
                      }}
                    >
                      ● แดง
                    </span>{" "}
                    = กำลังจะมาถึง
                    <span
                      style={{
                        marginLeft: 8,
                        color: STATUS_COLORS[STATUS.COMPLETED],
                        fontWeight: "bold",
                      }}
                    >
                      ● เขียว
                    </span>{" "}
                    = ผ่านมาแล้ว
                  </span>
                </div>
              </Card>

              {/* ✅ Calendar */}
              <Calendar
                value={currentDate}
                cellRender={cellRender}
                onSelect={handleDateSelect}
                onPanelChange={handlePanelChange}
                mode={panelMode}
              />
            </Col>
          </Row>
        </Spin>
      </Card>

      {/* ✅ Modal */}
      {isModal && (
        <AppointmentData
          open={isModal}
          close={handleCloseModal}
          dataRec={dataModal}
          date={dateSelect}
          panel={panelMode}
        />
      )}
    </>
  );
};

const AppointmentLawsuit = MotionHoc(Main);
export default AppointmentLawsuit;
